import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { Toaster, toast } from "sonner";
import { http, API, GATE_LOST_EVENT } from "./lib/api";
import { clearGate, gateHint, saveGateHint } from "./lib/gate";
import { SCREENS } from "./lib/constants";
import { I18nProvider, useI18n } from "./lib/i18n";
import { useTheme } from "./lib/theme";
import Topbar from "./components/Topbar";
import Sidebar from "./components/Sidebar";
import RedAlertBar from "./components/RedAlertBar";
import StewardModal from "./components/StewardModal";
import ApiKeyModal from "./components/ApiKeyModal";
import ReportModal from "./components/ReportModal";
import AuthPortal from "./components/screens/AuthPortal";
import ConnectSource from "./components/screens/ConnectSource";
import ExecutiveOS from "./components/screens/ExecutiveOS";
import IntakeSeal from "./components/screens/IntakeSeal";
import AiWorkspace from "./components/screens/AiWorkspace";
import CustodyLedger from "./components/screens/CustodyLedger";
import AuditTimeline from "./components/screens/AuditTimeline";
import ExceptionCenter from "./components/screens/ExceptionCenter";
import MatterWorkspace from "./components/screens/MatterWorkspace";
import DesignGallery from "./components/screens/DesignGallery";
import VerifyPage from "./components/VerifyPage";
import PolicyStudio from "./components/tabs/PolicyStudio";
import Deployment from "./components/tabs/Deployment";
import { GateStatusCard, DEMO_GATE_STATS } from "./components/GateStatusCard";
import { useConsoleData } from "./hooks/useConsoleData";
import { ScreenBoundary } from "./components/ScreenBoundary";
import { ExportModal } from "./components/ExportModal";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { reportMetrics } from "./lib/analytics";
import { buildDailyAccessReport } from "./lib/dailyReport";
import { nextMid } from "./lib/ids";
import { nowIsoUtc } from "./lib/time";

const INTRO = {
  zh: "您好，我是 Veralix Aegis 零信任助手，运行在只读推理平面中（无任何写权限）。可向我咨询案件证据可采性、异地登录风险排查、OWASP LLM 防御或 Rego 策略编写。",
  en: "I am the Veralix Aegis zero-trust copilot, running inside a read-only inference plane with no write scope. Ask me about evidence admissibility, impossible-travel triage, OWASP LLM defenses or Rego policy authoring.",
};

function Console() {
  const { t, lang } = useI18n();
  const [session, setSession] = useState(null);
  const [gate, setGate] = useState(() => {
    const hint = gateHint();
    return hint ? { expires_at: hint } : null;
  });
  const [screen, setScreen] = useState("exec");
  const [policyDraft, setPolicyDraft] = useState(null);
  const [clientHash, setClientHash] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [stewardTarget, setStewardTarget] = useState(null);
  const [signing, setSigning] = useState(false);
  const [apiKeyOpen, setApiKeyOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState({ seal: false, report: false, preserve: false });
  const [copilotMsgs, setCopilotMsgs] = useState([{ mid: nextMid(), role: "assistant", content: INTRO[lang] }]);
  const [copilotBusy, setCopilotBusy] = useState(false);
  const copilotSession = useRef(null);
  const mainRef = useRef(null);
  const [gateDemo, setGateDemo] = useState(true);
  const [exportState, setExportState] = useState(null);
  // Per-exception AEGIS forensic findings: { [exceptionId]: { status, text } }
  const [threatAnalyses, setThreatAnalyses] = useState({});

  const {
    status, setStatus, summary, pending, logs, sources, setSources, evidence, blocks, exceptions,
    matters, matterId, setMatterId, matterDetail, matterLoading, drills, health, gateStats, refresh,
  } = useConsoleData(!!session, screen);

  useEffect(() => {
    reportMetrics();
  }, []);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [screen]);

  useEffect(() => {
    setCopilotMsgs((m) => (m.length === 1 && m[0].role === "assistant" ? [{ mid: nextMid(), role: "assistant", content: INTRO[lang] }] : m));
  }, [lang]);

  useEffect(() => {
    const onLost = () => {
      clearGate();
      setGate(null);
      setSession(null);
      toast.error(t("gate.lost"));
    };
    window.addEventListener(GATE_LOST_EVENT, onLost);
    return () => window.removeEventListener(GATE_LOST_EVENT, onLost);
  }, [t]);

  // The gate token is an httpOnly cookie now, so only the server can confirm the session.
  useEffect(() => {
    if (!gateHint()) return;
    http
      .get("/gate/session")
      .then(({ data }) => {
        saveGateHint(data.expires_at);
        setGate((g) => ({ ...g, ...data }));
      })
      .catch(() => {
        clearGate();
        setGate(null);
      });
  }, []);

  const verifyGate = async (passcode) => {
    try {
      const { data } = await http.post("/gate/verify", { passcode, terminal: "AEGIS_CONSOLE_WEB" });
      saveGateHint(data.expires_at);
      setGate(data);
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        return { ok: false, reason: "locked", lockedFor: detail?.retry_after ?? 900 };
      }
      return { ok: false, reason: "invalid", attemptsRemaining: detail?.attempts_remaining ?? null };
    }
  };

  const relock = async () => {
    try {
      await http.post("/gate/lock");
    } catch (e) {
      console.warn("Server-side re-lock failed; clearing the local session anyway", e);
    }
    clearGate();
    setGate(null);
    setSession(null);
  };

  const handleExportDailyReport = async () => {
    setExportState({ busy: true, title: "生成中…", body: "正在从 WORM 账本汇总今日准入记录并加盖数字印章…" });
    try {
      const filename = await buildDailyAccessReport({
        stats: gateDemo ? DEMO_GATE_STATS : gateStats,
        demo: gateDemo,
        officerId: session?.officer?.officer_id,
        headBlock: blocks[0],
      });
      setExportState({ busy: false, title: "导出成功", body: `终端准入日报 (PDF) 已安全下载：${filename}` });
      setTimeout(() => setExportState(null), 2600);
    } catch (e) {
      console.error("Daily access report export failed", e);
      setExportState(null);
      toast.error(t("exp.failed"));
    }
  };

  const authenticate = async (pin) => {
    try {
      const { data } = await http.post("/auth/fido2", { pin });
      const srcs = await http.get("/sources").then((r) => r.data).catch(() => []);
      setSources(srcs);
      setSession(data);
      setScreen(srcs.some((s) => s.connected) ? "exec" : "source");
      toast.success(`${t("auth.ok")} · ${data.officer.officer_id}`);
      return true;
    } catch {
      return false;
    }
  };

  const connectSource = async (id) => {
    try {
      await http.post("/sources/connect", { source_id: id });
      toast.success(t("src.connected"));
      await refresh();
    } catch {
      toast.error("Source binding failed");
    }
  };

  const analyzeFile = async (file, mid) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("context", "Officer-submitted artifact via Aegis intake");
    fd.append("matter_id", mid || matterId);
    try {
      const { data } = await http.post("/forensics/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.evidence_id} · ${data.classification}`);
      refresh();
      return data;
    } catch {
      toast.error("Forensic intake failed");
      return null;
    }
  };

  const sealEvidence = async (id, mid, hash) => {
    try {
      const { data } = hash
        ? await http.post("/seal-evidence", {
            matter_id: mid || matterId,
            hash,
            operator: session?.officer?.officer_id || "STEWARD_0x9482",
            timestamp: nowIsoUtc(),
            evidence_id: id,
          })
        : await http.post(`/evidence/${id}/seal`);
      toast.success(t("seal.sealed", { n: data.seal_block ?? data.block?.index }));
      await refresh();
      return data;
    } catch {
      toast.error("Seal failed");
      return null;
    }
  };

  const sealAll = async () => {
    const unsealed = evidence.filter((e) => !e.sealed);
    if (unsealed.length === 0) {
      toast.info(t("seal.none"));
      return;
    }
    setBusy((b) => ({ ...b, seal: true }));
    for (const e of unsealed) await http.post(`/evidence/${e.evidence_id}/seal`).catch(() => null);
    setBusy((b) => ({ ...b, seal: false }));
    toast.success(`${unsealed.length} × ${t("exec.btn.seal")}`);
    refresh();
  };

  const generateReport = async () => {
    setBusy((b) => ({ ...b, report: true }));
    setReport(null);
    setReportOpen(true);
    try {
      const { data } = await http.post("/report/generate", { matter_id: matterId });
      setReport(data);
    } catch {
      toast.error("Report generation failed");
      setReportOpen(false);
    } finally {
      setBusy((b) => ({ ...b, report: false }));
      refresh();
    }
  };

  const ensureReport = async (mid) => {
    if (report && !mid) return report;
    const { data } = await http.post("/report/generate", { matter_id: mid || matterId });
    setReport(data);
    refresh();
    return data;
  };

  const generateReportForMatter = async (mid) => {
    setBusy((b) => ({ ...b, report: true }));
    setReport(null);
    setReportOpen(true);
    try {
      const { data } = await http.post("/report/generate", { matter_id: mid });
      setReport(data);
    } catch {
      toast.error("Report generation failed");
      setReportOpen(false);
    } finally {
      setBusy((b) => ({ ...b, report: false }));
      refresh();
    }
  };

  const preserveState = async () => {
    setBusy((b) => ({ ...b, preserve: true }));
    try {
      const { data } = await http.post("/state/preserve");
      setSnapshot(data);
      toast.success(`${data.snapshot_id} · #${data.block.index}`);
      refresh();
    } catch {
      toast.error("Preservation failed");
    } finally {
      setBusy((b) => ({ ...b, preserve: false }));
    }
  };

  const toggleLockdown = async (active) => {
    try {
      const { data } = await http.post("/lockdown/toggle", { active });
      setStatus(data);
      if (active) toast.error("CRITICAL LOCKDOWN engaged");
      else toast.success("Lockdown lifted");
      refresh();
    } catch {
      toast.error("Lockdown toggle failed");
    }
  };

  const simulate = async () => {
    try {
      const { data } = await http.post("/pending-actions/simulate");
      toast.warning(`${data.risk_level} · ${data.req_id}`);
      refresh();
    } catch {
      toast.error("Simulation failed");
    }
  };

  const submitSign = async (pin) => {
    if (!stewardTarget) return;
    setSigning(true);
    try {
      if (stewardTarget.kind === "tamper") {
        const { data } = await http.post("/worm/tamper", { pin });
        toast.error(t("drill.started", { n: data.tampered_block }));
      } else if (stewardTarget.kind === "restore") {
        const { data } = await http.post("/worm/restore", { pin });
        toast.success(t("drill.restored", { b: data.restored_blocks.join(", ") }));
      } else {
        const { data } = await http.post(`/exceptions/${stewardTarget.req_id}/resolve`, {
          pin,
          disposition: "CONTAINED",
        });
        toast.success(`HMAC committed · WORM #${data.block?.index ?? data.seal_block}`);
      }
      setStewardTarget(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signature failed");
    } finally {
      setSigning(false);
    }
  };

  const handleHandoff = (result) => {
    if (result?.target === "policy_studio") {
      setPolicyDraft(result.draft);
      setScreen("policy");
    } else {
      setScreen("exec");
    }
    refresh();
  };

  // Streams a live AEGIS forensic verdict for one exception, keyed by its real `id`.
  const analyzeThreat = async (item) => {
    const excId = item.id;
    const setAnalysis = (patch) =>
      setThreatAnalyses((prev) => ({ ...prev, [excId]: { ...prev[excId], ...patch } }));

    setAnalysis({ status: "running", text: "" });

    try {
      const res = await fetch(`${API}/copilot/threat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          threat_id: excId,
          threat_type: item.kind,
          case_id: matterId || "CASE-2026-0014A",
          risk_level: item.severity,
          description: `${item.title}: ${item.detail}`,
          telemetry: {
            target: item.target,
            digest: item.digest,
            detected_at: item.detected_at,
          },
        }),
      });

      if (!res.ok || !res.body) {
        setAnalysis({ status: "error", text: `[Aegis threat plane error ${res.status}]` });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let degraded = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.type === "delta" || data.type === "error") {
              if (data.type === "error") degraded = true;
              acc += data.content || "";
              setAnalysis({ text: acc });
            }
          } catch (err) {
            console.error("Malformed SSE frame:", err);
          }
        }
      }
      setAnalysis({ status: degraded ? "degraded" : "done" });
    } catch (e) {
      console.error("Threat analysis failed", e);
      setAnalysis({ status: "error", text: "[connection lost] Aegis threat analysis plane unreachable." });
    }
  };

  // Carries the completed AEGIS finding (if any) into the Steward sign-off modal for human review.
  const openStewardFor = (item) =>
    setStewardTarget({
      req_id: item.req_id || item.id,
      action_type: item.action_type || item.kind,
      risk_level: item.risk_level || (item.severity === "CRITICAL" ? "HIGH_RISK" : "MEDIUM_RISK"),
      digest: item.digest || "-",
      analysis: threatAnalyses[item.id]?.status === "done" ? threatAnalyses[item.id].text : null,
    });

  if (!gate || !session)
    return <AuthPortal gate={gate} onGate={verifyGate} onAuth={authenticate} onRelock={relock} />;

  const views = {
    auth: (
      <div className="rounded-xl border border-edge bg-panel/70 p-8 text-sm text-slate-400" data-testid="auth-session-panel">
        <p className="font-mono text-[11px] text-aegis-emerald">{session.officer.officer_id} · {session.hardware_key.serial}</p>
        <p className="mt-2">{session.authenticated_at} · WORM #{session.block?.index}</p>
        <p className="mt-4 font-mono text-[11px] text-slate-400 break-all">session {session.session_token}</p>
      </div>
    ),
    source: <ConnectSource sources={sources} onConnect={connectSource} onContinue={() => setScreen("exec")} />,
    exec: (
      <>
        <ExecutiveOS
          status={status}
          summary={summary}
          pending={pending}
          logs={logs}
          snapshot={snapshot}
          onSign={openStewardFor}
          onSimulate={simulate}
          onLockdown={() => toggleLockdown(true)}
          onSealAll={sealAll}
          onReport={generateReport}
          onPreserve={preserveState}
          onThreatIntercept={refresh}
          busy={busy}
        />
        <GateStatusCard
          stats={gateDemo ? DEMO_GATE_STATS : gateStats}
          demo={gateDemo}
          onToggleDemo={() => setGateDemo((d) => !d)}
          onExport={handleExportDailyReport}
          exporting={!!exportState?.busy}
        />
      </>
    ),
    intake: (
      <IntakeSeal
        evidence={evidence}
        matters={matters}
        matterId={matterId}
        setMatterId={setMatterId}
        clientHash={clientHash}
        setClientHash={setClientHash}
        onAnalyze={analyzeFile}
        onSeal={sealEvidence}
      />
    ),
    ai: (
      <AiWorkspace
        messages={copilotMsgs}
        setMessages={setCopilotMsgs}
        sessionRef={copilotSession}
        busy={copilotBusy}
        setBusy={setCopilotBusy}
        evidence={evidence}
        blocks={blocks}
        report={report}
        summary={summary}
        matter={matters.find((m) => m.matter_id === matterId)}
        officer={session.officer}
        clientHash={clientHash}
        onEnsureReport={ensureReport}
        onHandoff={handleHandoff}
      />
    ),
    custody: (
      <CustodyLedger
        blocks={blocks}
        drills={drills}
        onRefresh={refresh}
        onTamper={() =>
          setStewardTarget({
            kind: "tamper",
            req_id: "LEDGER-TAMPER-DRILL",
            action_type: "WORM_TAMPER_DRILL",
            risk_level: "HIGH_RISK",
            digest: "out-of-band payload digest overwrite",
          })
        }
        onRestore={() =>
          setStewardTarget({
            kind: "restore",
            req_id: "LEDGER-RESTORE",
            action_type: "WORM_DRILL_RESTORE",
            risk_level: "MEDIUM_RISK",
            digest: "re-apply original payload digests",
          })
        }
      />
    ),
    audit: <AuditTimeline logs={logs} />,
    exception: (
      <ExceptionCenter
        exceptions={exceptions}
        lockdown={!!status?.lockdown_active}
        onLockdown={toggleLockdown}
        analyses={threatAnalyses}
        onAnalyzeThreat={analyzeThreat}
        onResolve={openStewardFor}
      />
    ),
    matter: (
      <MatterWorkspace
        matters={matters}
        matterId={matterId}
        setMatterId={setMatterId}
        detail={matterDetail}
        loading={matterLoading}
        onReport={generateReportForMatter}
        onOpenExport={() => setScreen("ai")}
      />
    ),
    policy: <PolicyStudio incoming={policyDraft} onConsumed={() => setPolicyDraft(null)} />,
    deploy: <Deployment status={status} />,
    gallery: <DesignGallery />,
  };

  return (
    <div className="app-shell flex h-screen flex-col overflow-hidden">
      <div className="z-50 shrink-0">
        <RedAlertBar active={!!status?.lockdown_active} onLift={() => toggleLockdown(false)} />
        <Topbar
          status={status}
          session={session}
          health={health}
          onOpenApiKey={() => setApiKeyOpen(true)}
          onSignOut={relock}
        />
        <nav className="no-print no-scrollbar flex gap-1 overflow-x-auto border-b border-edge bg-panel/60 px-3 py-2 lg:hidden">
          {SCREENS.map((n) => {
            const Icon = n.icon;
            const active = screen === n.id;
            return (
              <button
                key={n.id}
                data-testid={`mnav-${n.id}`}
                onClick={() => setScreen(n.id)}
                className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "border border-aegis-cyan/30 bg-aegis-cyan/10 text-aegis-cyan"
                    : "border border-transparent text-slate-400"
                }`}
              >
                <span className="font-mono text-[10px] opacity-70">{n.num}</span>
                <Icon size={14} /> {t(n.key)}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="mx-auto flex min-h-0 w-full max-w-[1800px] flex-1">
        <Sidebar screen={screen} setScreen={setScreen} status={status} exceptionCount={exceptions.length} />
        <main ref={mainRef} className="min-w-0 flex-1 overflow-y-auto px-5 py-6 lg:px-8 lg:py-8">
          <ErrorBoundary>
            <ScreenBoundary screen={screen}>{views[screen]}</ScreenBoundary>
          </ErrorBoundary>
        </main>
      </div>

      <StewardModal target={stewardTarget} onClose={() => setStewardTarget(null)} onSign={submitSign} loading={signing} />
      <ApiKeyModal open={apiKeyOpen} onClose={() => setApiKeyOpen(false)} />
      <ExportModal state={exportState} onClose={() => setExportState(null)} />
      <ReportModal
        open={reportOpen}
        report={report}
        loading={busy.report}
        evidence={evidence}
        matter={matters.find((m) => m.matter_id === matterId)}
        officer={session.officer}
        clientHash={clientHash}
        onClose={() => setReportOpen(false)}
      />
    </div>
  );
}

export default function App() {
  const verify = new URLSearchParams(window.location.search).get("verify");
  const { theme } = useTheme();
  return (
    <I18nProvider>
      <Toaster
        theme={theme}
        position="bottom-right"
        toastOptions={{
          style: {
            background: theme === "dark" ? "#070A10" : "#FFFFFF",
            border: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.12)"}`,
            color: theme === "dark" ? "#cbd5e1" : "#334155",
            fontFamily: "Inter, sans-serif",
          },
        }}
      />
      {verify ? <VerifyPage index={verify} /> : <Console />}
    </I18nProvider>
  );
}
